import { MessageContext } from '@/contexts/MessageContext';
import { MessageCircle, ThumbsDown, ThumbsUp, X, ChevronDown } from 'lucide-react';
import { useCallback, useContext, useState, useEffect } from 'react';
import { useRecoilValue } from 'recoil';

import {
  IStep,
  firstUserInteraction,
  useChatSession
} from '@chainlit/react-client';

import Translator from '@/components/i18n/Translator';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

interface FeedbackButtonsProps {
  message: IStep;
}

export function FeedbackButtons({ message }: FeedbackButtonsProps) {
  const { onFeedbackUpdated, onFeedbackDeleted, showFeedbackButtons } =
    useContext(MessageContext);

  const [feedback, setFeedback] = useState<number | undefined>(
    message.feedback?.value
  );
  const [comment, setComment] = useState<string | undefined>(
    message.feedback?.comment
  );
  const [showDialog, setShowDialog] = useState<number>();
  const [commentInput, setCommentInput] = useState<string>('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [expandNegativeCategories, setExpandNegativeCategories] = useState(false);
  const firstInteraction = useRecoilValue(firstUserInteraction);
  const { idToResume } = useChatSession();

  const positiveFeedbackCategories = [
    'Helpful and relevant',
    'Accurate information',
    'Well-cited sources',
    'Comprehensive coverage',
    'Clear and concise',
    'Relevant to my query'
  ];

  const negativeFeedbackCategories = [
    'Citations are missing',
    'Citations are wrong',
    'The response is not from my data',
    'Inaccurate or irrelevant'
  ];

  const harmfulContentCategories = [
    'Hate speech stereotyping demeaning',
    'Violent glorification of violence self-harm',
    'Sexual explicit content grooming',
    'Manipulative devious emotional pushy bullying'
  ];

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // Restore feedback state when editing existing feedback
  useEffect(() => {
    console.log('[POS] useEffect triggered, showDialog:', showDialog, 'message.feedback:', message.feedback);
    
    if (showDialog !== undefined && message.feedback && message.feedback.value === showDialog) {
      const existingComment = message.feedback.comment || '';
      console.log('[POS] useEffect parsing existingComment:', existingComment);
      
      const categories: string[] = [];
      let userComment = '';

      // Parse pipe-separated format: category1:selected|category2:selected|comment:text
      if (existingComment) {
        // Check if it contains pipe-separated format
        if (existingComment.includes('|') || existingComment.includes(':')) {
          const parts = existingComment.split('|');
          console.log('[POS] useEffect split parts:', parts);
          
          for (const part of parts) {
            const trimmedPart = part.trim();
            if (trimmedPart.includes(':')) {
              const [key, value] = trimmedPart.split(':', 2);
              const trimmedKey = key.trim();
              const trimmedValue = value.trim();
              
              console.log('[POS] useEffect parsed key:', trimmedKey, 'value:', trimmedValue);
              
              if (trimmedKey === 'comment') {
                userComment = trimmedValue;
              } else if (trimmedValue === 'selected') {
                categories.push(trimmedKey);
              }
            } else {
              // Part without colon - treat as plain comment
              userComment = trimmedPart;
            }
          }
        } else {
          // Plain text comment (no pipe-separated format) - backward compatibility
          userComment = existingComment;
        }
      }

      console.log('[POS] useEffect setting categories:', categories);
      console.log('[POS] useEffect setting userComment:', userComment);
      
      setSelectedCategories(categories);
      setCommentInput(userComment);
      
      // Auto-expand harmful content section if any harmful category is selected
      const hasHarmfulCategory = categories.some(cat => 
        harmfulContentCategories.includes(cat)
      );
      if (hasHarmfulCategory && showDialog === 0) {
        setExpandNegativeCategories(true);
      }
    } else if (showDialog === undefined) {
      // Reset state when dialog closes
      console.log('[POS] useEffect resetting state');
      setSelectedCategories([]);
      setCommentInput('');
      setExpandNegativeCategories(false);
    }
  }, [showDialog, message.feedback]);

  if (!showFeedbackButtons) {
    return null;
  }

  const handleFeedbackChange = useCallback(
    (newFeedback?: number, newComment?: string) => {
      if (newFeedback === undefined) {
        if (onFeedbackDeleted && message.feedback?.id) {
          onFeedbackDeleted(
            message,
            () => {
              setFeedback(undefined);
              setComment(undefined);
            },
            message.feedback.id
          );
        }
      } else if (onFeedbackUpdated) {
        onFeedbackUpdated(
          message,
          () => {
            setFeedback(newFeedback);
            setComment(newComment);
          },
          {
            ...(message.feedback || {}),
            forId: message.id,
            threadId: message.threadId,
            value: newFeedback,
            comment: newComment
          }
        );
      }
    },
    [message, onFeedbackDeleted, onFeedbackUpdated]
  );

  const handleFeedbackClick = useCallback(
    (nextValue: number) => {
      // Always open dialog to allow editing/viewing existing feedback
      setShowDialog(nextValue);
    },
    []
  );

  const isDisabled = message.streaming || !(firstInteraction || idToResume);

  return (
    <div className="flex items-center">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              disabled={isDisabled}
              onClick={() => handleFeedbackClick(1)}
              className={
                feedback === 1
                  ? 'text-green-600 positive-feedback-on'
                  : 'text-muted-foreground positive-feedback-off'
              }
            >
              <ThumbsUp className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Give positive feedback
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              disabled={isDisabled}
              onClick={() => handleFeedbackClick(0)}
              className={
                feedback === 0
                  ? 'text-red-600 negative-feedback-on'
                  : 'text-muted-foreground negative-feedback-off'
              }
            >
              <ThumbsDown />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Give negative feedback
          </TooltipContent>
        </Tooltip>

        {comment && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                disabled={isDisabled}
                onClick={() => {
                  console.log('[POS] MessageCircle clicked, feedback value:', feedback);
                  console.log('[POS] message.feedback.comment:', message.feedback?.comment);
                  console.log('[POS] Local state comment:', comment);
                  
                  setShowDialog(feedback);
                }}
              >
                <MessageCircle />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <Translator path="chat.messages.feedback.edit" />
            </TooltipContent>
          </Tooltip>
        )}
      </TooltipProvider>

      <Dialog
        open={showDialog !== undefined}
        onOpenChange={() => {
          setShowDialog(undefined);
          setExpandNegativeCategories(false);
        }}
      >
        <DialogContent className="max-w-md rounded-md border border-gray-300 [&>button]:hidden">
          <DialogHeader className="pb-0.5">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg !font-bold text-black">
                Submit Feedback
              </DialogTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setShowDialog(undefined);
                  setExpandNegativeCategories(false);
                }}
                className="!rounded-none border-0 hover:bg-gray-100 hover:border hover:border-gray-300"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-2">
            <div>
              <p className="text-xs text-gray-500 mb-1 !font-light">
                Your feedback will improve this experience.
              </p>
              <p className="text-xs text-black mb-3 !font-light">
                {showDialog === 1 ? 'What made this response helpful?' : 'Why wasn\'t this response helpful?'}
              </p>
              
              <div className="space-y-2">
                {showDialog === 1 ? (
                  // Positive feedback - Button style
                  positiveFeedbackCategories.map((category) => (
                    <button
                      key={category}
                      onClick={() => handleCategoryToggle(category)}
                      className={`w-full px-4 py-2 text-left text-sm border rounded transition-colors ${
                        selectedCategories.includes(category)
                          ? 'bg-blue-500 border-blue-500 text-white'
                          : 'bg-white border-gray-300 text-black hover:bg-gray-50'
                      }`}
                    >
                      {category}
                    </button>
                  ))
                ) : (
                  // Negative feedback - Regular options only
                  <div className="space-y-2">
                    {negativeFeedbackCategories.map((category) => (
                      <button
                        key={category}
                        onClick={() => handleCategoryToggle(category)}
                        className={`w-full px-4 py-2 text-left text-sm border rounded transition-colors ${
                          selectedCategories.includes(category)
                            ? 'bg-red-500 border-red-500 text-white'
                            : 'bg-white border-gray-300 text-black hover:bg-gray-50'
                        }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="text-xs text-black mb-1 block !font-light">
                Additional Comments (Optional)
              </label>
              <textarea
                value={commentInput}
                onChange={(e) => setCommentInput(e.target.value)}
                placeholder={showDialog === 1 
                  ? "Please share any additional thoughts about what made this response helpful..."
                  : "Please describe the issue with this response..."}
                className="w-full min-h-[80px] p-3 text-sm border border-gray-300 !rounded-none resize-none focus:outline-none focus:ring-0 focus:border-gray-400 bg-white !font-normal"
                maxLength={200}
                style={{
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  lineHeight: '1.4'
                }}
              />
              <p className="text-xs text-gray-400 mt-1 !font-light">
                {commentInput.length}/200 characters
              </p>
            </div>
          </div>

          <div className="pt-2">
            <p className="text-xs text-gray-400 mb-3 !font-light">
              We'll share your feedback with AVA team. Thank you for helping us improve!            </p>
            
            <DialogFooter className="!flex !flex-row !justify-start !space-x-2 !space-y-0 p-0">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDialog(undefined);
                  setExpandNegativeCategories(false);
                }}
                className="!rounded-none border border-gray-300 bg-white text-black hover:bg-gray-50 !font-normal px-6 py-2 h-auto w-20 flex-shrink-0"
              >
                Cancel
              </Button>
              <Button
                id="submit-feedback"
                disabled={selectedCategories.length === 0 && commentInput.trim() === ''}
                onClick={() => {
                  if (showDialog !== undefined) {
                    // Build pipe-separated key:value format
                    const categoryPairs = selectedCategories.map(cat => `${cat}:selected`);
                    if (commentInput.trim()) {
                      categoryPairs.push(`comment:${commentInput.trim()}`);
                    }
                    const feedbackComment = categoryPairs.join('|');
                    handleFeedbackChange(showDialog, feedbackComment);
                  }
                  setShowDialog(undefined);
                }}
                className={`!rounded-none !font-normal px-6 py-2 h-auto w-20 flex-shrink-0 ${selectedCategories.length === 0 && commentInput.trim() === '' 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed hover:bg-gray-300' 
                  : 'bg-gray-800 text-white hover:bg-gray-900'}`}
              >
                Submit
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
